import { Zap, BookOpen } from 'lucide-react';
import type { ProcessingMode } from './types';

/**
 * F3 — Concise vs Detailed quality toggle.
 *
 * Mirrors frontend-prod's tab row at `features/draft/PromptInputScene.tsx:413-453`.
 * Maps to backend wire field `mode: 'speed' | 'accuracy'` (top-level on every
 * draft message payload).
 *
 * Constraint: when F1 Auto/Instant is active, Detailed is disabled and the
 * effective mode is clamped to Concise. The parent (TaskPane) enforces this
 * by passing `disabledDetailed`.
 */
export interface DraftQualityToggleProps {
  value: ProcessingMode;
  onChange: (next: ProcessingMode) => void;
  disabled?: boolean;
  disabledDetailed?: boolean;
}

const OPTIONS: ReadonlyArray<{
  value: ProcessingMode;
  title: string;
  subtitle: string;
  icon: typeof Zap;
}> = [
  {
    value: 'speed',
    title: 'Concise',
    subtitle: 'Produces concise drafts.',
    icon: Zap,
  },
  {
    value: 'accuracy',
    title: 'Detailed',
    subtitle: 'Produces detailed drafts.',
    icon: BookOpen,
  },
];

export function DraftQualityToggle({
  value,
  onChange,
  disabled = false,
  disabledDetailed = false,
}: DraftQualityToggleProps): JSX.Element {
  return (
    <div className="px-3 pt-1 pb-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
        Quality
      </div>
      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const selected = value === opt.value;
          const isOptionDisabled =
            disabled || (disabledDetailed && opt.value === 'accuracy');
          return (
            <button
              key={opt.value}
              type="button"
              disabled={isOptionDisabled}
              onClick={() => onChange(opt.value)}
              title={
                disabledDetailed && opt.value === 'accuracy'
                  ? 'Detailed is unavailable in Auto mode. Switch to Guided to enable.'
                  : opt.subtitle
              }
              className={[
                'flex flex-col items-start gap-1 rounded-md border px-2.5 py-2 text-left transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-ring',
                selected
                  ? 'border-primary bg-accent/60'
                  : 'border-border bg-card hover:bg-accent/40',
                isOptionDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              ].join(' ')}
              aria-pressed={selected}
            >
              <div className="flex items-center gap-1.5">
                <Icon
                  className={[
                    'w-3.5 h-3.5',
                    selected ? 'text-primary' : 'text-foreground/70',
                  ].join(' ')}
                />
                <span className="text-xs font-medium text-foreground leading-tight">
                  {opt.title}
                </span>
              </div>
              <span className="text-[10px] text-muted-foreground leading-snug">
                {opt.subtitle}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
